import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { RadioButton } from "primereact/radiobutton";
import { ProgressBar } from "primereact/progressbar";
import { Dialog } from "primereact/dialog";
import { useFormik } from "formik";
import { useToast } from "../../utils/toast";
import formService from "../../services/forms/form.service";
import { StratejikAnketRequestDto } from "../../services/forms/form.dto";
import ProtokolFirmalarComponent from "../../components/ProtokolFirmalarComponent";
import { useAuth } from "../../utils/auth";
import { useNavigate } from "react-router-dom";

const StratejikAnket = () => {
  const toast = useToast();
  const auth = useAuth();
  const navigate = useNavigate();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [questionsPerPage] = useState(1); // Her sayfada 1 stratejik alan (4 soru)

  // Sayfa yüklendiğinde kullanıcının daha önce anket gönderip göndermediğini kontrol et
  useEffect(() => {
    checkExistingSurvey();
  }, []);

  const checkExistingSurvey = async () => {
    try {
      const response = await formService.stratejikAnketKontrol();

      // ResponseDto<T> yapısına göre: { data: T, statusCode: number, error: string }

      if (response.result && !response.result.error) {
        if (response.result.data.has_submitted) {
          setHasSubmitted(true);
        } else {
          setHasSubmitted(false);
        }
      } else {
        console.log("No has_submitted found in response");
      }
    } catch (error: any) {
      console.error("Anket kontrolü sırasında hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitSurvey = async (values: StratejikAnketRequestDto) => {
    // Kullanıcının giriş yapıp yapmadığını kontrol et
    if (!auth.getUser().access_token) {
      toast.show("Anket doldurmak için giriş yapmalısınız.", "error");
      navigate("/loginsebis", { state: { from: window.location.pathname } });
      return;
    }

    try {
      const response = await formService.stratejikAnket(values);

      if (response.result && !response.result.error) {
        if (response?.result?.data) {
          toast.show("Stratejik anket başarıyla kaydedildi.", "success");
          formik.resetForm();
          setHasSubmitted(true);
          setShowConfirmDialog(false);
        } else {
          toast.show("Anket kaydedilemedi.", "error");
        }
      } else {
        console.log("Anket kaydedilemedi.");
      }
    } catch (error: any) {
      toast.show("Bir hata oluştu. Lütfen tekrar deneyin.", "error");
    }
  };

  const formik = useFormik<StratejikAnketRequestDto>({
    initialValues: {
      // Yeşil Dönüşüm
      yesil_hazirlik: undefined,
      yesil_algı: undefined,
      yesil_yetkinlik: undefined,
      yesil_ilerleme: undefined,

      // Dijital Dönüşüm
      dijital_hazirlik: undefined,
      dijital_algı: undefined,
      dijital_yetkinlik: undefined,
      dijital_ilerleme: undefined,

      // Eğitim
      egitim_hazirlik: undefined,
      egitim_algı: undefined,
      egitim_yetkinlik: undefined,
      egitim_ilerleme: undefined,

      // İş Geliştirme
      is_gelistirme_hazirlik: undefined,
      is_gelistirme_algı: undefined,
      is_gelistirme_yetkinlik: undefined,
      is_gelistirme_ilerleme: undefined,

      // Ar-Ge
      arge_hazirlik: undefined,
      arge_algı: undefined,
      arge_yetkinlik: undefined,
      arge_ilerleme: undefined,

      // Kapasite Geliştirme
      kapasite_hazirlik: undefined,
      kapasite_algı: undefined,
      kapasite_yetkinlik: undefined,
      kapasite_ilerleme: undefined,

      // Afet Yönetimi ve İş Sürekliliği
      afet_hazirlik: undefined,
      afet_algı: undefined,
      afet_yetkinlik: undefined,
      afet_ilerleme: undefined
    },
    validate: (values) => {
      const errors: any = {};

      // Tüm soruların cevaplanıp cevaplanmadığını kontrol et
      const requiredFields = [
        "yesil_hazirlik",
        "yesil_algı",
        "yesil_yetkinlik",
        "yesil_ilerleme",
        "dijital_hazirlik",
        "dijital_algı",
        "dijital_yetkinlik",
        "dijital_ilerleme",
        "egitim_hazirlik",
        "egitim_algı",
        "egitim_yetkinlik",
        "egitim_ilerleme",
        "is_gelistirme_hazirlik",
        "is_gelistirme_algı",
        "is_gelistirme_yetkinlik",
        "is_gelistirme_ilerleme",
        "arge_hazirlik",
        "arge_algı",
        "arge_yetkinlik",
        "arge_ilerleme",
        "kapasite_hazirlik",
        "kapasite_algı",
        "kapasite_yetkinlik",
        "kapasite_ilerleme",
        "afet_hazirlik",
        "afet_algı",
        "afet_yetkinlik",
        "afet_ilerleme"
      ];

      for (const field of requiredFields) {
        if (values[field as keyof StratejikAnketRequestDto] === undefined) {
          errors[field] = "Bu soru zorunludur.";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (hasSubmitted) {
        setShowConfirmDialog(true);
        return;
      }

      await submitSurvey(values);
    }
  });

  // İlerleme hesaplama
  const totalQuestions = 28;
  const answeredQuestions = Object.values(formik.values).filter((value) => value !== undefined).length;
  const progressPercentage = Math.round((answeredQuestions / totalQuestions) * 100 * 10) / 10;

  const anketSorulari = [
    {
      alan: "Yeşil Dönüşüm",
      icon: "pi-leaf",
      color: "green",
      aciklama: "Sürdürülebilir çevre ve yeşil teknolojiler",
      sorular: [
        {
          key: "yesil_hazirlik",
          soru: "OSB'mizde yeşil dönüşüm için gerekli olan altyapı yatırımları planlanmış durumdadır.",
          boyut: "Hazırlık"
        },
        {
          key: "yesil_algı",
          soru: "Yeşil dönüşüm, bölgemizin sürdürülebilirliği açısından hayati bir öneme sahiptir.",
          boyut: "Algı"
        },
        {
          key: "yesil_yetkinlik",
          soru: "OSB personelinin yeşil dönüşüm uygulamaları konusunda teknik bilgi düzeyi yeterlidir.",
          boyut: "Yetkinlik"
        },
        {
          key: "yesil_ilerleme",
          soru: "Son 12 ay içinde yeşil dönüşüm kapsamında somut adımlar atılmıştır.",
          boyut: "İlerleme"
        }
      ]
    },
    {
      alan: "Dijital Dönüşüm",
      icon: "pi-desktop",
      color: "blue",
      aciklama: "Dijital teknolojiler ve dijitalleşme süreçleri",
      sorular: [
        {
          key: "dijital_hazirlik",
          soru: "Dijital dönüşüm süreci için gerekli olan yazılım ve donanım altyapısına sahibiz.",
          boyut: "Hazırlık"
        },
        {
          key: "dijital_algı",
          soru: "Dijitalleşmenin, OSB'mizin rekabet gücünü artıracağına inanıyorum.",
          boyut: "Algı"
        },
        {
          key: "dijital_yetkinlik",
          soru: "OSB yönetiminde dijital araçları etkin kullanabilecek insan kaynağımız mevcut.",
          boyut: "Yetkinlik"
        },
        {
          key: "dijital_ilerleme",
          soru: "Dijitalleşme konusunda ölçülebilir ilerlemeler kaydettik.",
          boyut: "İlerleme"
        }
      ]
    },
    {
      alan: "Eğitim",
      icon: "pi-graduation-cap",
      color: "orange",
      aciklama: "Sürekli eğitim ve kapasite geliştirme",
      sorular: [
        {
          key: "egitim_hazirlik",
          soru: "OSB bünyesinde eğitim faaliyetleri için ayrılmış fiziki veya dijital altyapı vardır.",
          boyut: "Hazırlık"
        },
        {
          key: "egitim_algı",
          soru: "Sürekli eğitim, OSB'nin kurumsal gelişimi için kritik bir faktördür.",
          boyut: "Algı"
        },
        {
          key: "egitim_yetkinlik",
          soru: "Eğitim programlarını hazırlayıp yürütebilecek yeterli insan kaynağına sahibiz.",
          boyut: "Yetkinlik"
        },
        {
          key: "egitim_ilerleme",
          soru: "Geçtiğimiz yıl içinde yeşil dönüşüm veya dijitalleşme konularında eğitimler düzenlendi.",
          boyut: "İlerleme"
        }
      ]
    },
    {
      alan: "İş Geliştirme",
      icon: "pi-briefcase",
      color: "purple",
      aciklama: "Yeni iş fırsatları ve iş geliştirme stratejileri",
      sorular: [
        {
          key: "is_gelistirme_hazirlik",
          soru: "OSB'de firmaların iş geliştirme faaliyetlerini destekleyecek yapılar mevcuttur.",
          boyut: "Hazırlık"
        },
        {
          key: "is_gelistirme_algı",
          soru: "Yeni iş fırsatları yaratmak OSB'mizin öncelikli stratejik hedeflerinden biridir.",
          boyut: "Algı"
        },
        {
          key: "is_gelistirme_yetkinlik",
          soru: "İş geliştirme süreçlerini yönetebilecek uzman personelimiz vardır.",
          boyut: "Yetkinlik"
        },
        {
          key: "is_gelistirme_ilerleme",
          soru: "OSB'miz, son dönemde yeni iş birlikleri veya yatırım çekme girişimlerinde bulunmuştur.",
          boyut: "İlerleme"
        }
      ]
    },
    {
      alan: "Ar-Ge",
      icon: "pi-microscope",
      color: "red",
      aciklama: "Araştırma ve geliştirme faaliyetleri",
      sorular: [
        {
          key: "arge_hazirlik",
          soru: "OSB'de Ar-Ge faaliyetleri için gerekli fiziksel ve mali altyapı planlanmıştır.",
          boyut: "Hazırlık"
        },
        {
          key: "arge_algı",
          soru: "Ar-Ge, OSB'mizin uzun vadeli başarısı için olmazsa olmazdır.",
          boyut: "Algı"
        },
        {
          key: "arge_yetkinlik",
          soru: "OSB firmaları Ar-Ge projeleri hazırlayacak bilgi ve deneyime sahiptir.",
          boyut: "Yetkinlik"
        },
        {
          key: "arge_ilerleme",
          soru: "OSB firmalarının yürüttüğü Ar-Ge proje sayısı artış göstermektedir.",
          boyut: "İlerleme"
        }
      ]
    },
    {
      alan: "Kapasite Geliştirme",
      icon: "pi-users",
      color: "teal",
      aciklama: "İnsan kaynağı ve yönetim kapasitesi geliştirme",
      sorular: [
        {
          key: "kapasite_hazirlik",
          soru: "OSB yönetiminde kapasite geliştirme için tanımlanmış bir stratejik plan vardır.",
          boyut: "Hazırlık"
        },
        {
          key: "kapasite_algı",
          soru: "İnsan kaynağının gelişimi, OSB başarısı için kritik bir etmendir.",
          boyut: "Algı"
        },
        {
          key: "kapasite_yetkinlik",
          soru: "OSB yönetim ekibi kapasite geliştirme yöntemleri konusunda bilgili ve donanımlıdır.",
          boyut: "Yetkinlik"
        },
        {
          key: "kapasite_ilerleme",
          soru: "Son bir yılda yönetim kadrosuna yönelik kapasite geliştirme uygulamaları gerçekleştirilmiştir.",
          boyut: "İlerleme"
        }
      ]
    },
    {
      alan: "Afet Yönetimi ve İş Sürekliliği",
      icon: "pi-shield",
      color: "indigo",
      aciklama: "Afet hazırlığı ve iş sürekliliği planları",
      sorular: [
        {
          key: "afet_hazirlik",
          soru: "OSB'mizde afet yönetimi ve iş sürekliliği planları hazırlanmış durumdadır.",
          boyut: "Hazırlık"
        },
        {
          key: "afet_algı",
          soru: "Olası afetler karşısında hazırlıklı olmak, sürdürülebilirlik açısından zorunludur.",
          boyut: "Algı"
        },
        {
          key: "afet_yetkinlik",
          soru: "OSB yönetimi, afet yönetimi süreçlerini yürütecek yeterlilikte donanıma sahiptir.",
          boyut: "Yetkinlik"
        },
        {
          key: "afet_ilerleme",
          soru: "Afet yönetimi tatbikatları ve iş sürekliliği çalışmaları son 12 ayda uygulanmıştır.",
          boyut: "İlerleme"
        }
      ]
    }
  ];

  // Sayfa hesaplamaları
  const totalPages = anketSorulari.length; // Her sayfada 1 stratejik alan
  const currentStratejikAlan = anketSorulari[currentPage];

  // Mevcut sayfadaki soruların tamamlanıp tamamlanmadığını kontrol et
  const isCurrentPageComplete = currentStratejikAlan ? 
    currentStratejikAlan.sorular.every(soru => 
      formik.values[soru.key as keyof StratejikAnketRequestDto] !== undefined
    ) : false;

  const renderRadioButtons = (fieldName: string) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {[1, 2, 3, 4, 5].map((value) => (
          <div
            key={value}
            className="flex flex-column align-items-center p-3 border-1 surface-border border-round cursor-pointer hover:surface-100 transition-colors transition-duration-150"
            onClick={() => formik.setFieldValue(fieldName, value)}
          >
            <RadioButton
              inputId={`${fieldName}_${value}`}
              name={fieldName}
              value={value}
              onChange={(e) => formik.setFieldValue(fieldName, e.value)}
              checked={formik.values[fieldName as keyof StratejikAnketRequestDto] === value}
            />
            <label htmlFor={`${fieldName}_${value}`} className="text-center text-base mt-2 cursor-pointer">
              <div className="text-sm text-600">
                {value === 1
                  ? "Kesinlikle katılmıyorum"
                  : value === 2
                    ? "Katılmıyorum"
                    : value === 3
                      ? "Kararsızım"
                      : value === 4
                        ? "Katılıyorum"
                        : "Kesinlikle katılıyorum"}
              </div>
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileRadioButtons = (fieldName: string) => {
    return (
      <div className="flex flex-column gap-2">
        {[1, 2, 3, 4, 5].map((value) => (
          <div
            key={value}
            className="flex align-items-center p-3 border-1 surface-border border-round cursor-pointer hover:surface-100 transition-colors transition-duration-150"
            onClick={() => formik.setFieldValue(fieldName, value)}
          >
            <RadioButton
              inputId={`${fieldName}_${value}`}
              name={fieldName}
              value={value}
              onChange={(e) => formik.setFieldValue(fieldName, e.value)}
              checked={formik.values[fieldName as keyof StratejikAnketRequestDto] === value}
            />
            <label htmlFor={`${fieldName}_${value}`} className="flex align-items-center ml-3 cursor-pointer flex-1">
              <span className="text-900 text-base">
                {value === 1
                  ? "Kesinlikle katılmıyorum"
                  : value === 2
                    ? "Katılmıyorum"
                    : value === 3
                      ? "Kararsızım"
                      : value === 4
                        ? "Katılıyorum"
                        : "Kesinlikle katılıyorum"}
              </span>
            </label>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="w-full surface-card text-center p-6">
          <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3"></i>
          <p className="text-600">Anket durumu kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card p-2 md:p-4 p-1 md:p-4">
        <div className="w-full surface-card">
          {/* Header */}
          <div className="text-center mb-2 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-primary-50 to-primary-100 border-round">
            <div className="text-900 text-xl md:text-2xl lg:text-4xl font-bold mb-2 md:mb-3">Stratejik Anket</div>
            <p className="text-600 text-xs md:text-sm lg:text-lg leading-relaxed px-2 md:px-0">
              OSB'nizin stratejik alanlardaki durumunu değerlendirmek için aşağıdaki soruları yanıtlayınız.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4 p-3 bg-white border-round shadow-1">
            <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between mb-2 gap-2">
              <span className="text-900 font-semibold text-sm">Anket İlerlemesi</span>
              <span className="text-600 text-xs">
                {hasSubmitted ? (
                  <span className="text-yellow-600 font-medium flex align-items-center">
                    <i className="pi pi-check-circle mr-1"></i>
                    <span className="whitespace-nowrap">Daha önce doldurdunuz</span>
                  </span>
                ) : (
                  <span className="whitespace-nowrap">
                    {answeredQuestions}/{totalQuestions} soru yanıtlandı
                  </span>
                )}
              </span>
            </div>
            {hasSubmitted ? (
              <div className="bg-green-50 border-1 border-green-200 border-round p-3">
                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3">
                  <div className="flex align-items-center flex-1">
                    <i className="pi pi-check-circle text-green-600 mr-2 text-lg"></i>
                    <span className="text-green-800 font-medium text-sm md:text-base leading-relaxed">
                      Bu anketi daha önce doldurdunuz. İsterseniz yeni bir anket doldurabilirsiniz.
                    </span>
                  </div>
                  <Button
                    label="Yeni Anket Doldur"
                    icon="pi pi-refresh"
                    className="p-button-sm p-button-outlined p-button-success whitespace-nowrap"
                    onClick={() => {
                      formik.resetForm();
                      setHasSubmitted(false);
                    }}
                  />
                </div>
              </div>
            ) : (
              <ProgressBar value={progressPercentage} className="h-2rem" />
            )}
          </div>

          {/* Anket Formu - Sadece doldurulmamışsa göster */}
          {!hasSubmitted && (
            <>
              {/* Giriş Yapma Uyarısı - Kullanıcı giriş yapmamışsa sadece bu gösterilir */}
              {!auth.getUser().access_token ? (
                <div className="bg-orange-50 border-1 border-orange-200 border-round p-4 text-center">
                  <div className="flex flex-column align-items-center">
                    <i className="pi pi-exclamation-triangle text-orange-600 text-4xl mb-3"></i>
                    <h3 className="text-orange-800 font-bold text-xl mb-2">Anket Doldurmak İçin Giriş Yapın</h3>
                    <p className="text-orange-700 text-base mb-4">
                      Bu anketi doldurmak için hesabınıza giriş yapmanız gerekmektedir.
                    </p>
                    <Button
                      label="Giriş Yap"
                      icon="pi pi-sign-in"
                      className="p-button-lg p-button-orange"
                      onClick={() => navigate("/loginsebis", { state: { from: window.location.pathname } })}
                    />
                  </div>
                </div>
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  {/* Sayfa Navigasyonu */}
                  <div className="flex justify-content-between align-items-center mb-4 p-3 bg-white border-round shadow-1">
                <div className="flex align-items-center gap-2">
                  <span className="text-600 text-sm">Sayfa:</span>
                  <span className="font-bold text-900">{currentPage + 1} / {totalPages}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-chevron-left"
                    className="p-button-sm p-button-outlined"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    tooltip="Önceki Sayfa"
                  />
                </div>
              </div>

              {/* Mevcut Sayfa Stratejik Alanı */}
              {currentStratejikAlan && (
                <Card className="mb-3 shadow-1 border-round">
                  {/* Ana Başlık */}
                  <div className="flex align-items-center mb-4 p-4 bg-gradient-to-r from-primary-50 to-primary-100 border-round">
                    <div className={`p-3 border-round mr-4 bg-${currentStratejikAlan.color}-50`}>
                      <i className={`pi ${currentStratejikAlan.icon} text-2xl text-${currentStratejikAlan.color}-600`}></i>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-900 mb-2">{currentStratejikAlan.alan}</h2>
                      {currentStratejikAlan.aciklama && (
                        <p className="text-600 text-base">{currentStratejikAlan.aciklama}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="flex flex-column align-items-end gap-2">
                        <span className="text-base text-500">
                          Sayfa {currentPage + 1} / {totalPages}
                        </span>
                        {isCurrentPageComplete ? (
                          <div className="flex align-items-center text-green-600">
                            <i className="pi pi-check-circle mr-2 text-lg"></i>
                            <span className="text-base font-medium">Tamamlandı</span>
                          </div>
                        ) : (
                          <div className="flex align-items-center text-orange-600">
                            <i className="pi pi-exclamation-triangle mr-2 text-lg"></i>
                            <span className="text-base font-medium">4 soruyu yanıtlayın</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 4 Soru Tipi */}
                  <div className="grid grid-cols-1 gap-2">
                    {currentStratejikAlan.sorular.map((soru, soruIndex) => (
                      <div
                        key={soruIndex}
                        className={`p-4 border-1 surface-border border-round bg-white hover:shadow-1 transition-all transition-duration-200 ${
                          formik.values[soru.key as keyof StratejikAnketRequestDto] === undefined 
                            ? 'border-orange-300 bg-orange-50' 
                            : 'border-green-300 bg-green-50'
                        }`}
                      >
                        <div className="flex align-items-center mb-3">
                          <span
                            className={`text-base font-bold text-white px-3 py-1 border-round-full bg-${currentStratejikAlan.color}-500`}
                          >
                            {soru.boyut}
                          </span>
                          <span className="text-600 text-sm ml-2">Soru {soruIndex + 1}/4</span>
                          {formik.values[soru.key as keyof StratejikAnketRequestDto] !== undefined && (
                            <i className="pi pi-check-circle text-green-600 ml-auto text-lg"></i>
                          )}
                        </div>
                        <p className="text-900 font-medium mb-2 text-lg leading-relaxed">{soru.soru}</p>
                        {/* Desktop Radio Buttons */}
                        <div className="hidden md:block">{renderRadioButtons(soru.key)}</div>
                        {/* Mobile Radio Buttons */}
                        <div className="block md:hidden">{renderMobileRadioButtons(soru.key)}</div>
                        {formik.errors[soru.key as keyof StratejikAnketRequestDto] && (
                          <small className="p-error block mt-2">
                            {formik.errors[soru.key as keyof StratejikAnketRequestDto]}
                          </small>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Alt Navigasyon Butonları */}
              <div className="flex justify-content-between align-items-center mt-4 mb-3">
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-chevron-left"
                    className="p-button-sm p-button-outlined"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    tooltip="Önceki Sayfa"
                  />
                </div>
                
                <div className="flex gap-2">
                  {currentPage === totalPages - 1 ? (
                    <Button
                      label="Anketi Gönder"
                      type="submit"
                      className="p-button-success px-4 py-2 text-base"
                      disabled={formik.isSubmitting || !isCurrentPageComplete}
                      icon="pi pi-send"
                      iconPos="right"
                    />
                  ) : (
                    <Button
                      label="İleri"
                      icon="pi pi-chevron-right"
                      iconPos="right"
                      className="p-button"
                      disabled={!isCurrentPageComplete}
                      onClick={() => {
                        setCurrentPage(currentPage + 1);
                        // Sayfanın en üstüne yönlendir
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                          // Alternatif yöntem
                          document.documentElement.scrollTop = 0;
                          document.body.scrollTop = 0;
                        }, 100);
                      }}
                    />
                  )}
                </div>
              </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
      <ProtokolFirmalarComponent />
      {/* Onay Dialog'u */}
      <Dialog
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        header="Anket Güncelleme Onayı"
        className="w-90 md:w-50"
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="İptal"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowConfirmDialog(false)}
            />
            <Button
              label="Güncelle"
              icon="pi pi-check"
              className="p-button-success"
              onClick={() => submitSurvey(formik.values)}
            />
          </div>
        }
      >
        <div className="flex align-items-center mb-3">
          <i className="pi pi-exclamation-triangle text-yellow-600 text-2xl mr-3"></i>
          <div>
            <h3 className="text-lg font-bold mb-2">Dikkat!</h3>
            <p className="text-600 mb-3">
              Bu anketi daha önce gönderdiniz. Mevcut cevaplarınızı güncellemek istediğinizden emin misiniz?
            </p>
            <p className="text-sm text-500">Not: Yeni gönderim, önceki cevaplarınızın üzerine yazılacaktır.</p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default StratejikAnket;
